import * as React from 'react';
import { Link } from 'react-router-dom';

import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { IFigureTable } from '../../api/figure-contracts';
import { figuresApi } from '../../api/figures-api';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import type {
  IAdminHubAffiliation,
  IAdminParticipantDetail,
  IAdminParticipantSecAdvFirm
} from '../../api/participant-contracts';
import { secAdvApi } from '../../api/sec-adv-api';
import type { IWorkItemEvent, IWorkItemRow } from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { absent } from '../../components/account-facts';
import { useAdminData } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import {
  recordAreaOf,
  recordPath,
  recordSurveyPath,
  searchAcross,
  type RecordArea
} from '../../components/participant-link';
import { ResponseStatusChip } from '../../components/responses-table';
import { formatDollars } from '../../components/sec-adv-values';
import { responsePath, workItemPath } from '../../paths';
import { WorkItemStateChip } from '../compliance/work-items-table';
import { DdChip, useDdRows } from './dd-chip';
import { liveFirst } from './household';
import type { ISurveyTab, RecordLayout } from './person-record';
import type { IRosterRead } from './roster';
import type { IOpenItemsRead } from './work-items';

/**
 * A record page's summary area (SR-400, design `2026-10-07-record-pages-person-team-firm.md` §12):
 * what a reader needs before anything else, in the record card's right column on every tab
 * (SR-406) — the record's
 * compliance state, its surveys at a glance, and for a team its lead and assets, for a firm its
 * regulatory AUM. Each line reads survey-api and states what it read, never a verdict of its own.
 *
 * **A person's surveys include their teams' and firms'** (SR-414's D23, SR-413): under their own,
 * "Through teams and firms" names each affiliation's current response of each survey — its newest
 * live one, else its newest — "TEAMS 2027 submitted (The Roberts Group)" — from the hub's
 * `affiliations`, the same member Related records draws; nothing where the hub sends none.
 *
 * **The compliance line is the queue's** (SR-400's AC1, AC2, AC4): the record's unclosed work
 * items (`open`, `pending_firm`, `escalated`) as Work items read them, handed up (its first page,
 * earliest due first), each its state's chip and a link to the item, and
 * for an escalated one who escalated and when — the newest `decided` event whose decision is
 * `escalate`, from the item's own history, since the row carries `decidedAt` but not who. Once an
 * item is decided it is closed, leaves this line, and its outcome stays in Work items' compliance
 * history; it follows Work items' reads — the page's write cue and its own "Read again" — never
 * the poll.
 *
 * **The team's lead and assets** (SR-400's C3, C4 rulings): slot 1 of the team's roster as
 * survey-api resolved it, handed up by the Advisors section's read (never a second read) — a link to the person's page, or the name as answered and "not
 * resolved"; and the newest Team Cust Assets cell of the TEAMS figures table, labelled with the
 * metric's own name and its as-of date — the table's rows come newest first, so the first filled
 * cell is the newest; nothing is summed or computed. **A firm's AUM** is the SEC ADV register's
 * RAUM (Part 1A 5.F, the register card's), read only under `registry.browse`, said per firm matched.
 */
export function RecordSummary({
  participant,
  layout,
  tabs,
  search,
  written,
  roster,
  openItems,
  canReadInternal,
  affiliations = []
}: {
  participant: IAdminParticipantDetail;
  layout: RecordLayout;
  tabs: ISurveyTab[];
  search: string;
  written: number;
  /** The team's roster as the Advisors section read it — handed up, never read a second time. */
  roster: IRosterRead | undefined;
  /** The record's first page of open work items, as Work items read it — handed up likewise. */
  openItems: IOpenItemsRead | undefined;
  /** `internal_fields.read` — the figures' permission, as Veritant research's; without it no Assets
   *  line, never a refused read. */
  canReadInternal: boolean;
  /** A person's teams and firms, the hub's (`[]` for any other record, or an older API). */
  affiliations?: readonly IAdminHubAffiliation[];
}) {
  return (
    <section aria-label="Summary" data-record-summary className="grid gap-2 text-sm">
      <ComplianceLine read={openItems} written={written} />
      <SurveysLine
        participantId={participant.participantId}
        area={recordAreaOf({ recordType: layout })}
        tabs={tabs}
        affiliations={affiliations}
        search={search}
        written={written}
        canReadInternal={canReadInternal}
      />
      {layout === 'team' && (
        <>
          <LeadLine read={roster} search={search} from="teams" />
          {canReadInternal && (
            <TeamAssetsLine participantId={participant.participantId} written={written} />
          )}
        </>
      )}
      {(layout === 'firm' || layout === 'org') && (
        <RegisterAumLine firms={participant.secAdvFirms ?? []} />
      )}
    </section>
  );
}

const LINK = 'text-[var(--color-sapphire)] underline-offset-4 hover:underline';

/** One labelled line of the summary. */
function SummaryLine({
  label,
  name,
  children
}: {
  label: string;
  /** The `data-summary` hook a spec addresses the line by. */
  name: string;
  children: React.ReactNode;
}) {
  return (
    <div data-summary={name} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-xs tracking-wide text-muted-foreground uppercase">{label}</span>
      {children}
    </div>
  );
}

/** Who escalated an item and when — its newest `escalate` decision, from its history. */
interface IEscalation {
  by: Nullable<string>;
  at: string;
}

function escalationOf(events: IWorkItemEvent[]): Nullable<IEscalation> {
  // Newest first, as survey-api sends an item's history.
  const event = events.find((each) => each.kind === 'decided' && each.decision === 'escalate');
  return event ? { by: event.actor.email, at: event.at } : null;
}

function ComplianceLine({
  read,
  written
}: {
  read: IOpenItemsRead | undefined;
  written: number;
}) {
  const items = read?.items ?? null;
  const error = read?.error ?? null;
  const more = items !== null && read?.total != null ? read.total - items.length : 0;
  // Only an escalated item asks its history, for who escalated it — the one read this line makes.
  const escalatedIds = (items ?? [])
    .filter((row) => row.state === 'escalated')
    .map((row) => row.id)
    .join(' ');
  const escalations = useAdminData(
    async (signal) => {
      // Each its own read: one that fails leaves the others said, and is said itself.
      const found = new Map<string, Nullable<IEscalation> | 'failed'>();
      await Promise.all(
        escalatedIds
          .split(' ')
          .filter(Boolean)
          .map(async (id) => {
            try {
              const detail = await workItemsApi.getWorkItem(id, signal);
              found.set(id, detail ? escalationOf(detail.body.events) : null);
            } catch {
              found.set(id, 'failed');
            }
          })
      );
      return found;
    },
    // Re-read on a write too: another escalation moves who and when under the same ids.
    [escalatedIds, written]
  );

  return (
    <SummaryLine label="Compliance" name="compliance">
      {items === null ? (
        <span role={error ? 'alert' : undefined}>
          {absent(error ? `The compliance queue could not be read: ${error}` : 'Reading…')}
        </span>
      ) : items.length === 0 ? (
        <span data-compliance="clear">{absent('Not in the compliance queue.')}</span>
      ) : (
        <ul aria-label="In the compliance queue" className="grid gap-1">
          {items.map((row) => (
            <ComplianceItem key={row.id} row={row} escalation={escalations.data?.get(row.id)} />
          ))}
          {more > 0 && (
            <li className="text-muted-foreground">
              {more === 1 ? 'One more' : `${more} more`} under Work items
            </li>
          )}
        </ul>
      )}
    </SummaryLine>
  );
}

function ComplianceItem({
  row,
  escalation
}: {
  row: IWorkItemRow;
  escalation: Nullable<IEscalation> | 'failed' | undefined;
}) {
  return (
    <li data-compliance-item={row.id} className="flex flex-wrap items-center gap-2">
      <WorkItemStateChip state={row.state} />
      <Link
        to={workItemPath(row.id)}
        className={LINK}
      >
        {row.subject}
      </Link>
      <span className="text-muted-foreground">{row.surveyCode} queue</span>
      {row.state === 'escalated' &&
        (escalation === 'failed' ? (
          <span className="text-muted-foreground">Who escalated it could not be read.</span>
        ) : (
          escalation && (
            <span data-escalated-by className="text-muted-foreground">
              Escalated by {escalation.by ?? 'an account since removed'} on{' '}
              {formatDateTime(escalation.at)}
            </span>
          )
        ))}
    </li>
  );
}

/** Each survey the record holds — its current response's cycle and status (said "In the recycle
 *  bin" where the survey has no live one), a link to its tab, and its DD chip (`dd-chip.tsx`). */
function SurveysLine({
  participantId,
  area,
  tabs,
  affiliations,
  search,
  written,
  canReadInternal
}: {
  participantId: string;
  /** The list the record page is drawn under — its survey tabs' addresses. */
  area: RecordArea;
  tabs: ISurveyTab[];
  affiliations: readonly IAdminHubAffiliation[];
  search: string;
  written: number;
  canReadInternal: boolean;
}) {
  const dd = useDdRows(
    participantId,
    tabs.map((tab) => tab.code),
    written,
    canReadInternal
  );
  const through = affiliations.flatMap((affiliation) =>
    newestPerSurvey(liveFirst(affiliation.responses)).map((response) => ({
      affiliation,
      response
    }))
  );
  return (
    <SummaryLine label="Surveys" name="surveys">
      {tabs.length === 0 ? (
        through.length === 0 && absent('No survey held.')
      ) : (
        // Beside the label, a long survey's DD chip wrapping inside it rather than pushing it down.
        <ul aria-label="Surveys held" className="flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-1">
          {tabs.map((tab) => (
            <li key={tab.code} data-summary-survey={tab.code} className="flex flex-wrap items-center gap-2">
              <Link
                to={`${recordSurveyPath(participantId, tab.code, area)}${search}`}
                className={LINK}
              >
                {tab.name}
              </Link>
              <span className="text-muted-foreground">{tab.latest.cycle.name}</span>
              <ResponseStatusChip status={tab.latest.status} />
              {tab.latest.status === 'recycled' && (
                <span data-summary-bin className="text-muted-foreground">
                  In the recycle bin
                </span>
              )}
              <DdChip
                row={dd?.get(tab.code)}
                participantId={participantId}
                surveyCode={tab.code}
                area={area}
                search={search}
              />
            </li>
          ))}
        </ul>
      )}
      {through.length > 0 && (
        <div data-summary-through className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-muted-foreground">Through teams and firms:</span>
          <ul aria-label="Surveys through teams and firms" className="flex flex-wrap gap-x-4 gap-y-1">
            {through.map(({ affiliation, response }) => (
              <li key={response.responseId} className="flex items-center gap-2">
                <Link to={responsePath(response.responseId)} className={LINK}>
                  {response.survey.code} {response.cycle.code}
                </Link>
                <ResponseStatusChip status={response.status} />
                <span>
                  (
                  <Link
                    to={`${recordPath(affiliation.participantId, affiliation)}${searchAcross(
                      search,
                      area,
                      recordAreaOf(affiliation)
                    )}`}
                    className={LINK}
                  >
                    {affiliation.displayName}
                  </Link>
                  )
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SummaryLine>
  );
}

/** Each survey's current response — the first of each, the list coming live first (`liveFirst`)
 *  and newest change first within that. */
function newestPerSurvey(responses: readonly IOversightResponseRow[]): IOversightResponseRow[] {
  const seen = new Set<string>();
  return responses.filter((response) => {
    if (seen.has(response.survey.code)) return false;
    seen.add(response.survey.code);
    return true;
  });
}

/** A team's lead advisor — slot 1 of its roster as survey-api resolved it, from the Advisors
 *  section's own read. */
function LeadLine({
  read,
  search,
  from
}: {
  read: IRosterRead | undefined;
  search: string;
  /** The list the page is drawn under — a lead is a person, under People. */
  from: RecordArea;
}) {
  const data = read?.roster ?? null;
  const error = read?.error ?? null;
  const lead = data?.members.find((member) => member.slot === 1);
  return (
    <SummaryLine label="Lead" name="lead">
      {data === null ? (
        absent(error ? `The roster could not be read: ${error}` : 'Reading…')
      ) : lead === undefined ? (
        absent('No submitted Teams survey names a lead yet.')
      ) : lead.member ? (
        <Link
          to={`${recordPath(lead.member.participantId, lead.member)}${searchAcross(
            search,
            from,
            recordAreaOf(lead.member)
          )}`}
          className={LINK}
        >
          {lead.member.displayName}
        </Link>
      ) : (
        <span>
          {lead.fullName ?? absent('No name answered')}{' '}
          <span className="text-muted-foreground">— not resolved to a person</span>
        </span>
      )}
    </SummaryLine>
  );
}

/** The newest filled cell of a figures table's column, with its as-of date. */
function newestCell(
  table: IFigureTable,
  metric: string,
  scope: string
): Nullable<{ label: string; asOf: string; value: number }> {
  const column = table.metrics.findIndex(
    (each) => each.metric === metric && each.scope === scope
  );
  if (column < 0) return null;
  // Rows come newest first; the first with a value is the newest.
  for (const row of table.rows) {
    const value = row.cells[column]?.value;
    if (typeof value === 'number') {
      return { label: table.metrics[column].label, asOf: row.asOf, value };
    }
  }
  return null;
}

/** A team's assets — the newest Team Cust Assets figure the TEAMS table holds. */
function TeamAssetsLine({ participantId, written }: { participantId: string; written: number }) {
  const { data, error } = useAdminData(
    (signal) => figuresApi.table(participantId, 'TEAMS', signal),
    [participantId, written]
  );
  const newest = data ? newestCell(data, 'cust_assets', 'team') : null;
  return (
    <SummaryLine label="Assets" name="assets">
      {data === null ? (
        absent(error ? `The figures could not be read: ${error}` : 'Reading…')
      ) : newest === null ? (
        absent('No team assets figure held.')
      ) : (
        <span>
          {formatDollars(newest.value)}{' '}
          <span className="text-muted-foreground">
            {newest.label}, as of {formatDay(newest.asOf)}
          </span>
        </span>
      )}
    </SummaryLine>
  );
}

/** A firm's regulatory AUM — the SEC ADV register's RAUM for each firm survey-api matched. */
function RegisterAumLine({ firms }: { firms: IAdminParticipantSecAdvFirm[] }) {
  const canBrowse = useCan()('registry.browse');
  if (firms.length === 0 || !canBrowse) return null;
  return (
    <SummaryLine label="RAUM" name="raum">
      <ul aria-label="RAUM by register firm" className="flex flex-wrap gap-x-4 gap-y-1">
        {firms.map((firm) => (
          <RegisterAum key={firm.firmCrd} firm={firm} />
        ))}
      </ul>
    </SummaryLine>
  );
}

function RegisterAum({ firm }: { firm: IAdminParticipantSecAdvFirm }) {
  // Wrapped: `getFirm` answers null for a 404, which is a loaded "no such firm", never "Reading…".
  const { data, error } = useAdminData(
    async (signal) => ({ firm: await secAdvApi.getFirm(firm.firmCrd, signal) }),
    [firm.firmCrd]
  );
  const total = data?.firm?.summary.raum?.total ?? null;
  return (
    <li data-raum={firm.firmCrd}>
      {data === null
        ? absent(error ? `The register could not be read: ${error}` : 'Reading…')
        : data.firm === null
          ? absent('No such firm in the register any more')
          : total === null
            ? absent('Not answered')
            : formatDollars(total)}{' '}
      <span className="text-muted-foreground">SEC ADV 5.F, {firm.legalName}</span>
    </li>
  );
}
